import React, { useMemo, useState } from 'react';
import { Modal, Select, Radio } from 'antd';
import ResultTable from '../../components/table/ResultTable';
import ResultHeader from '../../components/table/ResultHeader';
import ResultBody from '../../components/table/ResultBody';
import ResultRow from '../../components/table/ResultRow';
import ResultHeaderCell from '../../components/table/ResultHeaderCell';
import ResultBodyCell from '../../components/table/ResultBodyCell';
import HecRasHelper from '../../helpers/HecRasHelper';

/**
 * Render score with color
 */
const renderScore = (score) => {
    const numScore = parseFloat(score);
    let color = '#52c41a'; // green
    if (numScore < 0.7) color = '#faad14'; // yellow
    if (numScore < 0.5) color = '#ff4d4f'; // red
    return <span style={{ color, fontWeight: 'bold' }}>{score.toFixed(2)}</span>;
};

export default function ScoreAnalysisModal({ visible, onClose, history }) {
    const [selectedMessageCodes, setSelectedMessageCodes] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('crossSection'); // 'crossSection', 'bridge', 'culvert'
    
    const analysisData = useMemo(() => {
        if (!history) return null;

        // Full inventory list from backend (each item: { name, type, value })
        const itemList = history?.summary?.base?.item_list || [];

        // All raw messages from history operations
        const messages = HecRasHelper.extractMessages(history);

        // Allowed error_id sets per category
        const ALLOWED = {
            crossSection: HecRasHelper.ALLOWED_CROSS_SECTION_CODES,
            bridge: HecRasHelper.ALLOWED_BRIDGE_CODES,
            culvert: HecRasHelper.ALLOWED_CULVERT_CODES,
        };

        // Type matchers — backend may send 'CrossSection', 'Cross Section', 'Bridge', 'Culvert' etc.
        const normalizeType = (t) => (t == null ? '' : String(t).toLowerCase().replace(/\s+/g, ''));
        const typeOf = (item) => {
            const t = normalizeType(item?.type);
            if (t === 'bridge') return 'bridge';
            if (t === 'culvert') return 'culvert';
            return 'crossSection';
        };

        // Group raw messages by river station name -> Set of error_ids
        const errorIdsByName = {};
        for (const msg of messages) {
            const name = msg?.riverStation;
            if (name == null) continue;
            const key = String(name);
            if (!errorIdsByName[key]) errorIdsByName[key] = new Set();
            if (msg.errorId) errorIdsByName[key].add(msg.errorId);
        }

        // Build a row for one item from item_list
        const buildRow = (item, category) => {
            const name = item.name;
            const allowed = ALLOWED[category];
            const total = allowed.length;

            const allErrorIds = errorIdsByName[String(name)] || new Set();
            const matched = [...allErrorIds].filter(eid =>
                HecRasHelper.matchesCode(eid, allowed, category)
            );

            const score = total > 0
                ? Math.max(0, Math.min(1, (total - matched.length) / total))
                : 1;

            return {
                riverStation: name,
                score,
                messages: matched.sort().join(', '),
                messageCount: matched.length,
                type: category === 'bridge' ? 'Bridge'
                    : category === 'culvert' ? 'Culvert'
                    : 'Cross Section',
            };
        };

        const crossSectionData = [];
        const bridgeData = [];
        const culvertData = [];

        for (const item of itemList) {
            const category = typeOf(item);
            const row = buildRow(item, category);
            if (category === 'bridge') bridgeData.push(row);
            else if (category === 'culvert') culvertData.push(row);
            else crossSectionData.push(row);
        }

        // Sort by river station descending (DS first)
        const sortByRS = (a, b) => parseFloat(b.riverStation) - parseFloat(a.riverStation);
        crossSectionData.sort(sortByRS);
        bridgeData.sort(sortByRS);
        culvertData.sort(sortByRS);

        const allAllowedCodes = [
            ...new Set([
                ...ALLOWED.crossSection,
                ...ALLOWED.bridge,
                ...ALLOWED.culvert,
            ])
        ].sort();

        return {
            crossSectionData,
            bridgeData,
            culvertData,
            crossSectionCount: crossSectionData.length,
            bridgeCount: bridgeData.length,
            culvertCount: culvertData.length,
            allowedMessageCodes: allAllowedCodes,
        };
    }, [history]);
    
    // Get current data based on selected category
    const currentData = useMemo(() => {
        if (!analysisData) return [];
        if (selectedCategory === 'bridge') return analysisData.bridgeData;
        if (selectedCategory === 'culvert') return analysisData.culvertData;
        return analysisData.crossSectionData;
    }, [analysisData, selectedCategory]);
    
    // Get unique message codes from current data
    const messageCodeOptions = useMemo(() => {
        if (!currentData || currentData.length === 0) return [];
        const codes = new Set();
        currentData.forEach(item => {
            item.messages?.split(', ').forEach(code => {
                if (code.trim()) codes.add(code.trim());
            });
        });
        return Array.from(codes).sort().map(code => ({ label: code, value: code }));
    }, [currentData]);
    
    // Filter data based on selections
    const filteredData = useMemo(() => {
        if (!currentData || currentData.length === 0) return [];
        
        return currentData.filter(item => {
            // Filter by message codes
            if (selectedMessageCodes.length > 0) {
                const itemCodes = item.messages?.split(', ').map(c => c.trim()) || [];
                if (!selectedMessageCodes.some(code => itemCodes.includes(code))) {
                    return false;
                }
            }
            return true;
        });
    }, [currentData, selectedMessageCodes]);
    
    if (!history) {
        return (
            <Modal
                title="Score Analysis"
                open={visible}
                onCancel={onClose}
                footer={null}
                width={800}
                styles={{ body: { maxHeight: '70vh', overflow: 'auto' } }}
            >
                <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                    No history data available. Please run a check first.
                </div>
            </Modal>
        );
    }
    
    return (
        <Modal
            title="Score Analysis"
            open={visible}
            onCancel={onClose}
            footer={null}
            width={650}
            styles={{ body: { maxHeight: '70vh', overflow: 'auto' } }}
        >
            <div style={{ marginBottom: 12 }}>
                <Radio.Group 
                    value={selectedCategory} 
                    onChange={(e) => {
                        setSelectedCategory(e.target.value);
                        setSelectedMessageCodes([]); // Clear message code filter when category changes
                    }}
                    buttonStyle="solid"
                >
                    <Radio.Button value="crossSection">Cross Section</Radio.Button>
                    <Radio.Button value="bridge">Bridge</Radio.Button>
                    <Radio.Button value="culvert">Culvert</Radio.Button>
                </Radio.Group>
            </div>
            
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <Select
                    size='small'
                    mode="multiple"
                    allowClear
                    placeholder="Filter by Message Code..."
                    value={selectedMessageCodes}
                    onChange={setSelectedMessageCodes}
                    options={messageCodeOptions}
                    style={{ flex: 1 }}
                    maxTagCount={3}
                    showSearch
                    filterOption={(input, option) =>
                        option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                />
            </div>
            
            {(() => {
                const total = currentData?.length || 0;
                const avg = total > 0
                    ? currentData.reduce((acc, r) => acc + r.score, 0) / total
                    : 1;
                const categoryLabel = selectedCategory === 'bridge' ? 'Bridge'
                    : selectedCategory === 'culvert' ? 'Culvert'
                    : 'Cross Section';
                const uniqueCodes = new Set();
                (currentData || []).forEach(item => {
                    item.messages?.split(', ').forEach(code => {
                        const c = code.trim();
                        if (c) uniqueCodes.add(c);
                    });
                });
                let avgColor = '#52c41a';
                if (avg < 0.7) avgColor = '#faad14';
                if (avg < 0.5) avgColor = '#ff4d4f';
                const cardStyle = {
                    flex: 1,
                    padding: '12px 16px',
                    background: '#fff',
                    border: '1px solid #f0f0f0',
                    borderRadius: 6,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                };
                const labelStyle = { fontSize: 12, color: '#8c8c8c', marginBottom: 4 };
                const valueStyle = { fontSize: 22, fontWeight: 600, lineHeight: 1.2 };
                return (
                    <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                        <div style={cardStyle}>
                            <div style={labelStyle}>Total {categoryLabel}</div>
                            <div style={{ ...valueStyle, color: '#262626' }}>{total}</div>
                        </div>
                        <div style={cardStyle}>
                            <div style={labelStyle}>Unique Message IDs</div>
                            <div style={{ ...valueStyle, color: '#262626' }}>{uniqueCodes.size}</div>
                        </div>
                        <div style={cardStyle}>
                            <div style={labelStyle}>Avg Score</div>
                            <div style={{ ...valueStyle, color: avgColor }}>{avg.toFixed(2)}</div>
                        </div>
                    </div>
                );
            })()}

            {(!currentData || currentData.length === 0) ? (
                <div style={{ textAlign: 'center', padding: 20, color: '#999' }}>No data available for this category</div>
            ) : (
                <ResultTable scrollable={{ height: 400 }} style={{ width: '100%' }} maxWidth={'100%'}>
                    <ResultHeader>
                        <ResultRow>
                            <ResultHeaderCell scrollable={{ top: 0 }} style={{ textAlign: 'left', width: HecRasHelper.getWidth(10) }}>River Station</ResultHeaderCell>
                            {/* <ResultHeaderCell scrollable={{ top: 0 }} style={{ textAlign: 'left', width: HecRasHelper.getWidth(10) }}>Type</ResultHeaderCell> */}
                            <ResultHeaderCell scrollable={{ top: 0 }} style={{ textAlign: 'center', width: HecRasHelper.getWidth(6) }}>Score</ResultHeaderCell>
                            <ResultHeaderCell scrollable={{ top: 0 }} style={{ textAlign: 'left', width: HecRasHelper.getWidth(60) }}>Messages</ResultHeaderCell>
                        </ResultRow>
                    </ResultHeader>
                    <ResultBody>
                        {filteredData.map((row, index) => (
                            <ResultRow key={`${row.type}-${row.riverStation}-${index}`}>
                                <ResultBodyCell style={{ textAlign: 'left', width: HecRasHelper.getWidth(10) }}>{row.riverStation}</ResultBodyCell>
                                {/* <ResultBodyCell style={{ textAlign: 'left', width: HecRasHelper.getWidth(10) }}>{row.type}</ResultBodyCell> */}
                                <ResultBodyCell style={{ textAlign: 'center', width: HecRasHelper.getWidth(6) }}>{renderScore(row.score)}</ResultBodyCell>
                                <ResultBodyCell style={{ textAlign: 'left', width: HecRasHelper.getWidth(60) }}>{row.messages}</ResultBodyCell>
                            </ResultRow>
                        ))}
                    </ResultBody>
                </ResultTable>
            )}
            
            {(() => {
                const codesByCategory = {
                    crossSection: HecRasHelper.ALLOWED_CROSS_SECTION_CODES,
                    bridge: HecRasHelper.ALLOWED_BRIDGE_CODES,
                    culvert: HecRasHelper.ALLOWED_CULVERT_CODES,
                };
                const categoryCodes = [...(codesByCategory[selectedCategory] || [])].sort();
                if (categoryCodes.length === 0) return null;
                return (
                <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                    <div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: 13 }}>
                        Message Codes ({categoryCodes.length}):
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {categoryCodes.map(code => (
                            <span 
                                key={code} 
                                style={{ 
                                    padding: '2px 4px', 
                                    backgroundColor: '#fff', 
                                    border: '1px solid #d9d9d9', 
                                    borderRadius: 4,
                                    fontSize: 12
                                }}
                            >
                                {code}
                            </span>
                        ))}
                    </div>
                </div>
                );
            })()}
        </Modal>
    );
}
